import { inject, Injectable } from '@angular/core';
import { combineLatest, concatMap, map, forkJoin, Observable, of, shareReplay, switchMap, take, tap, catchError } from 'rxjs';
import { StateService } from '../shared/state.service';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../app.config';
import { ChargeItem, Invoice, ServiceRequest } from 'fhir/r4';
import type { DetailActionButton } from '../detailz-viewz/detailz-viewz.component';
import { AuthService } from '../shared/auth/auth.service';
import { inferServiceRequestType } from '../shared/service-request-type.util';

export type InferredServiceRequestType = 'imaging' | 'procedure' | 'surgery' | 'laboratory' | 'observation' | 'others';

@Injectable({
  providedIn: 'root'
})
export class ServiceRequestsService {
  stateService = inject(StateService);
  http = inject(HttpClient);
  backendApiEndPoint = inject(backendEndPointToken);
  private auth = inject(AuthService);

  constructor() { }

  ngOnInit() { }

  getChargeItemAndInvoice(
    patientId: string,
    serviceRequestId: string | null = null,
    chargeItems: ChargeItem[] | null = null,
    invoices: Invoice[] | null = null
  ): Observable<{ chargeItems: ChargeItem[], invoices: Invoice[] }> {

    return combineLatest({
      invoices: invoices ? of(invoices) : this.getInvoicesForServiceRequest(patientId),
      chargeItems: (chargeItems ? of(chargeItems) : this.getChargeItemsForServiceRequest(patientId)).pipe(
        map((chargeItems: ChargeItem[]) => {
          return serviceRequestId ? chargeItems.filter(ci => {
            return ci.service?.some((ref: any) => ref.reference === `ServiceRequest/${serviceRequestId}`);
          }) : chargeItems;
        })
      ),
    }).pipe(
      map(({ invoices, chargeItems }) => {
        const chargeItemsRefs = chargeItems.map(ci => `ChargeItem/${ci.id}`);

        return {
          chargeItems,
          invoices: serviceRequestId ? invoices.filter((inv: Invoice) => {
            const invoiceHasChargeItem = inv.lineItem?.some((line) => line.chargeItemReference && line.chargeItemReference?.reference && chargeItemsRefs.includes(line.chargeItemReference.reference));
            return invoiceHasChargeItem;
          }) : invoices
        }
      }),
      catchError(error => {
        console.error('Error fetching charge items and invoices:', error);
        return of({ chargeItems: [], invoices: [] });
      })
    );
  }
  getPerformingCapacityForAUser(serviceCode: string, serviceCategoryCode: string) {
    return of(true);
  }

  private normalize(v: any): string {
    return (v == null ? '' : String(v)).trim().toLowerCase();
  }

  private normalizeTabType(v: any): InferredServiceRequestType | '' {
    const t = this.normalize(v);
    if (!t) return '';
    if (t === 'lab-tests' || t === 'lab' || t === 'laboratory') return 'laboratory';
    if (t === 'imaging') return 'imaging';
    if (t === 'procedure') return 'procedure';
    if (t === 'surgery') return 'surgery';
    if (t === 'observation' || t === 'observations' || t === 'vital' || t === 'vitals' || t === 'vital-signs') return 'observation';
    if (t === 'others') return 'others';
    return '';
  }

  /**
   * Shared visibility check for action buttons on ServiceRequest/LabRequest details.
   * Note: we pre-filter actions here so feature components can decide what to show
   * (including capability gating when `capabilities` are provided on the action).
   */
  isActionVisible$(
    action: DetailActionButton,
    serviceRequest: ServiceRequest,
    context?: { currentTabType?: string }
  ): Observable<boolean> {
    const visibility = (action as any)?.visibility as
      | {
        includedStatus?: string[];
        excludedStatus?: string[];
        includedServiceRequestTypes?: string[];
        excludedServiceRequestTypes?: string[];
      }
      | undefined;

    const includedStatus = (visibility?.includedStatus || []).map((s) => this.normalize(s)).filter(Boolean);
    const excludedStatus = (visibility?.excludedStatus || []).map((s) => this.normalize(s)).filter(Boolean);
    if (includedStatus.length && excludedStatus.length) {
      console.warn('Action visibility config invalid: both includedStatus and excludedStatus set', action?.key);
      return of(false);
    }

    const includedTypes = (visibility?.includedServiceRequestTypes || []).map((s) => this.normalize(s)).filter(Boolean);
    const excludedTypes = (visibility?.excludedServiceRequestTypes || []).map((s) => this.normalize(s)).filter(Boolean);
    if (includedTypes.length && excludedTypes.length) {
      console.warn('Action visibility config invalid: both includedServiceRequestTypes and excludedServiceRequestTypes set', action?.key);
      return of(false);
    }

    const status = this.normalize((serviceRequest as any)?.status);
    const inferredType = this.normalize(inferServiceRequestType(serviceRequest as any)) as InferredServiceRequestType | '';
    const contextType = this.normalizeTabType(context?.currentTabType);

    // If the resource is missing category info, fall back to the current tab.
    const effectiveType = (inferredType && inferredType !== 'others') ? inferredType : (contextType || inferredType);

    // Optional capability gate: if the button specifies capabilities, require at least one to pass.
    const caps = Array.isArray((action as any)?.capabilities) ? ((action as any).capabilities as any[]) : [];
    if (caps.length) {
      const allowed = caps.some((c) => c?.resource && c?.action && this.auth.can(c.resource, c.action));
      if (!allowed) return of(false);
    }

    const serviceCode = this.normalize((serviceRequest as any)?.code?.coding?.[0]?.code);
    const serviceCategoryCode = this.normalize((serviceRequest as any)?.category?.[0]?.coding?.[0]?.code);

    return this.getPerformingCapacityForAUser(serviceCode, serviceCategoryCode).pipe(
      map((isCapable: boolean) => {
        if (!isCapable) return false;

        if (includedStatus.length && !includedStatus.includes(status)) return false;
        if (excludedStatus.length && excludedStatus.includes(status)) return false;

        if (includedTypes.length && !includedTypes.includes(this.normalize(effectiveType))) return false;
        if (excludedTypes.length && excludedTypes.includes(this.normalize(effectiveType))) return false;

        return true;
      })
    );
  }

  filterVisibleActions$(
    actions: DetailActionButton[],
    serviceRequest: ServiceRequest,
    context?: { currentTabType?: string }
  ): Observable<DetailActionButton[]> {
    const safe = Array.isArray(actions) ? actions : [];
    if (!safe.length) return of([]);

    return forkJoin(safe.map((a) => this.isActionVisible$(a, serviceRequest, context))).pipe(
      map((flags: boolean[]) => safe.filter((_, i) => flags[i] === true))
    );
  }
  isButtonDisplayable(authConfig: {
    serviceRequest: ServiceRequest, includedStatus?: string[], excludedStatus?: string[], includedTypeOfService?: string[],
    excludedTypeOfService?: string[]

  }): Observable<boolean> {
    if (authConfig.includedStatus && authConfig.includedStatus.length && authConfig.excludedStatus && authConfig.excludedStatus.length) {
      throw new Error('Cannot have both includedStatus and excludedStatus');
    }
    if (authConfig.includedTypeOfService && authConfig.includedTypeOfService.length && authConfig.excludedTypeOfService && authConfig.excludedTypeOfService.length) {
      throw new Error('Cannot have both includedTypeOfService and excludedTypeOfService');
    }
    const code = authConfig.serviceRequest.code?.coding?.[0]?.code || '';
    const status = authConfig.serviceRequest.status || '';
    const serviceCategoryCode = authConfig.serviceRequest.category?.[0]?.coding?.[0]?.code || '';

    const isUserCapable$ = this.getPerformingCapacityForAUser(code, serviceCategoryCode);

    return isUserCapable$.pipe(
      map((isCapable: boolean) => {
        if (!isCapable) {
          return false;
        }
        if (authConfig.includedStatus && authConfig.includedStatus.length) {
          if (!authConfig.includedStatus.includes(status)) {
            return false;
          }
        }
        if (authConfig.excludedStatus && authConfig.excludedStatus.length) {
          if (authConfig.excludedStatus.includes(status)) {
            return false;
          }
        }
        if (authConfig.includedTypeOfService && authConfig.includedTypeOfService.length) {
          if (!authConfig.includedTypeOfService.includes(serviceCategoryCode)) {
            return false;
          }
        }
        if (authConfig.excludedTypeOfService && authConfig.excludedTypeOfService.length) {
          if (authConfig.excludedTypeOfService.includes(serviceCategoryCode)) {
            return false;
          }
        }
        return true;
      })
    );
  }
  getInvoicesForServiceRequest(patientId: string): Observable<any> {
// $#$    // /* alert('called getInvoicesForServiceRequest...'); */
// $#$    // /* alert(patientId + " , " + this.stateService.currentPatientId$.getValue() */)
    let toUse = patientId === this.stateService.currentPatientId$.getValue() ?
      this.stateService.PatientResources.invoices.pipe(
        take(1),
        concatMap((invoices: any[]) => {
          return invoices.length > 0 ? of(invoices) :
            this.http.get(`${this.backendApiEndPoint}/Invoice?subject=Patient/${patientId}&_count=200`).pipe(
              tap((res: any) => {
                this.stateService.processBundleTransaction(res);
              }),
              map((res: any) => res.entry?.map((entry: any) => ({
                actualResource: entry.resource,
                savedStatus: 'saved',
                referenceId: this.stateService.toRefId(entry.resource) || entry.resource?.id || null
              })) || [])
            );
        })
      ) :
      this.stateService.orgWideResources.invoices.pipe(
        take(1),
        concatMap((invoices) => {
          const patientsOwnInvoices = invoices.filter(inv => {
            const patientRef = inv.actualResource.subject?.reference;
            return patientRef === `Patient/${patientId}`;
          });
          return invoices.length > 0 && patientsOwnInvoices.length > 0 ? of(patientsOwnInvoices) :
            this.http.get(`${this.backendApiEndPoint}/Invoice?_count=200`).pipe(
              tap((res: any) => {
                this.stateService.processOrgWideBundleTransaction(res);
              }),
              map((res: any) => res.entry?.map((entry: any) => ({
                actualResource: entry.resource,
                savedStatus: 'saved',
                referenceId: this.stateService.toRefId(entry.resource) || entry.resource?.id || null
              })) || [])
            );
        })
      );

    return toUse.pipe(
      map((res: any) => Array.isArray(res) ? res.map((item: any) => item.actualResource) : res?.actualResource)
    );
  }

  getChargeItemsForServiceRequest(patientId: string): Observable<ChargeItem[]> {
    let toUse = patientId === this.stateService.currentPatientId$.getValue() ?
      this.stateService.PatientResources.chargeItems.pipe(
        take(1),
        concatMap((chargeItem) => {
          return chargeItem.length > 0 ? of(chargeItem) :
            this.http.get(`${this.backendApiEndPoint}/ChargeItem?subject=Patient/${patientId}&_count=1000`).pipe(
              tap((res: any) => {
                this.stateService.processBundleTransaction(res);
              }),
              map((res: any) => res.entry?.map((entry: any) => ({
                actualResource: entry.resource,
                savedStatus: 'saved',
                referenceId: this.stateService.toRefId(entry.resource) || entry.resource?.id || null
              })) || [])
            )
        })
      ) :
      this.stateService.orgWideResources.chargeItems.pipe(
        take(1),
        concatMap((chargeItem) => {
          const patientsOwnChargeItems = chargeItem.filter(ci => {
            const subjectRef = ci.actualResource.subject?.reference;
            return subjectRef === `Patient/${patientId}`;
          });
          return chargeItem.length > 0 && patientsOwnChargeItems.length > 0 ? of(patientsOwnChargeItems) :
            this.http.get(`${this.backendApiEndPoint}/ChargeItem?_count=1000`).pipe(
              tap((res: any) => {
                this.stateService.processOrgWideBundleTransaction(res);
              }),
              map((res: any) => res.entry?.map((entry: any) => ({
                actualResource: entry.resource,
                savedStatus: 'saved',
                referenceId: this.stateService.toRefId(entry.resource) || entry.resource?.id || null
              })) || [])
            )
        })
      );

    return toUse.pipe(
      map((res: any) => Array.isArray(res) ? res.map((item: any) => item.actualResource) : res?.actualResource)
    );
  }
}
